import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Pagination,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Divider,
  Grid,
  Card,
  CardContent,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Pending as PendingIcon,
  HourglassEmpty as HourglassEmptyIcon,
  ShoppingCart as ShoppingCartIcon,
  AccountBalance as AccountBalanceIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import apiClient from '../lib/api';
import { toast } from 'react-hot-toast';

const OrdersList = ({ searchParams: searchParamsProp }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0,
    hasNext: false,
    hasPrev: false,
  });
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [orderDetails, setOrderDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  const loadOrders = async (page = pagination.page) => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.getOrders({ 
        page,
        limit: pagination.limit 
      });
      if (response.success) {
        setOrders(response.orders || []);
        if (response.pagination) {
          setPagination({
            page: response.pagination.page || page,
            limit: response.pagination.limit || 10,
            total: response.pagination.total || 0,
            pages: response.pagination.pages || 0,
            hasNext: response.pagination.hasNext || false,
            hasPrev: response.pagination.hasPrev || false,
          });
        }
      } else {
        throw new Error(response.message || 'Failed to load orders');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to load orders';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOrders(1);
  }, []);

  // Auto-open detail modal if orderId is in URL
  useEffect(() => {
    const orderId = searchParams.get('orderId');
    if (orderId && !detailModalOpen && selectedOrderId !== orderId) {
      handleOpenDetail(orderId);
      // Remove orderId from URL after opening
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('orderId');
      setSearchParams(newParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handlePageChange = (event, newPage) => {
    loadOrders(newPage);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'failed':
        return 'error';
      case 'pending':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon fontSize="small" />;
      case 'failed':
        return <ErrorIcon fontSize="small" />;
      case 'pending':
        return <HourglassEmptyIcon fontSize="small" />;
      default:
        return <PendingIcon fontSize="small" />;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleString();
    } catch {
      return dateString;
    }
  };

  const formatCurrency = (amount) => {
    if (!amount || !Number.isFinite(amount)) return 'N/A';
    return `$${(amount / 100).toFixed(2)}`;
  };

  const handleOpenDetail = async (paymentIntentId) => {
    setSelectedOrderId(paymentIntentId);
    setDetailModalOpen(true);
    setLoadingDetails(true);
    setOrderDetails(null);

    try {
      const response = await apiClient.getOrder(paymentIntentId);
      if (response.success) {
        setOrderDetails(response.order);
      } else {
        throw new Error(response.message || 'Failed to load order details');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to load order details';
      toast.error(errorMessage);
      setOrderDetails(null);
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleCloseDetail = () => {
    setDetailModalOpen(false);
    setSelectedOrderId(null);
    setOrderDetails(null);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert 
        severity="error" 
        action={
          <IconButton onClick={loadOrders} size="small">
            <RefreshIcon />
          </IconButton>
        }
      >
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" component="h2" sx={{ fontWeight: 600 }}>
          Orders
        </Typography>
        <Tooltip title="Refresh">
          <IconButton onClick={loadOrders} color="primary">
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {orders.length === 0 && !loading ? (
        <Alert severity="info">No orders found.</Alert>
      ) : (
        <>
          <TableContainer component={Paper} elevation={1}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Payment Intent ID</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                  <TableCell><strong>Amount</strong></TableCell>
                  <TableCell><strong>Steps</strong></TableCell>
                  <TableCell><strong>Created</strong></TableCell>
                  <TableCell><strong>Updated</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {orders.map((order) => (
                  <TableRow 
                    key={order.paymentIntentId} 
                    hover
                    onClick={() => handleOpenDetail(order.paymentIntentId)}
                    sx={{
                      cursor: 'pointer',
                    }}
                  >
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                      {order.paymentIntentId}
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={getStatusIcon(order.status)}
                        label={order.status || 'unknown'}
                        color={getStatusColor(order.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {order.amount ? formatCurrency(order.amount) : 'N/A'}
                    </TableCell>
                    <TableCell>
                      {order.steps ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          {Object.entries(order.steps).map(([step, status]) => (
                            <Chip
                              key={step}
                              label={`${step}: ${status}`}
                              size="small"
                              color={status === 'completed' ? 'success' : status === 'failed' ? 'error' : 'default'}
                              sx={{ fontSize: '0.7rem', height: 20 }}
                            />
                          ))}
                        </Box>
                      ) : (
                        'N/A'
                      )}
                    </TableCell>
                    <TableCell>{formatDate(order.createdAt)}</TableCell>
                    <TableCell>{formatDate(order.updatedAt)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          
          {pagination.pages > 0 && (
            <Stack spacing={2} sx={{ mt: 3, alignItems: 'center' }}>
              <Pagination
                count={pagination.pages}
                page={pagination.page}
                onChange={handlePageChange}
                color="primary"
                showFirstButton
                showLastButton
              />
              <Typography variant="body2" color="text.secondary">
                Showing {orders.length} of {pagination.total} orders
              </Typography>
            </Stack>
          )}
        </>
      )}

      {/* Order Detail Modal */}
      <Dialog
        open={detailModalOpen}
        onClose={handleCloseDetail}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ShoppingCartIcon color="primary" />
            <Typography variant="h6">
              Order Details
            </Typography>
            {selectedOrderId && (
              <Typography variant="caption" sx={{ fontFamily: 'monospace', ml: 2, color: 'text.secondary' }}>
                {selectedOrderId}
              </Typography>
            )}
          </Box>
        </DialogTitle>
        <DialogContent>
          {loadingDetails ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : orderDetails ? (
            <Box>
              {/* Order Status */}
              <Alert 
                severity={
                  orderDetails.status === 'completed' ? 'success' :
                  orderDetails.status === 'failed' ? 'error' : 'warning'
                } 
                sx={{ mb: 3 }}
              >
                <Typography variant="body2">
                  <strong>Status:</strong> {orderDetails.status || 'unknown'}
                </Typography>
              </Alert>

              {/* Order Information */}
              <Card variant="outlined" sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                    Order Information
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2"><strong>Payment Intent ID:</strong></Typography>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                        {orderDetails.paymentIntentId}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2"><strong>Amount:</strong></Typography>
                      <Typography variant="body2">
                        {formatCurrency(orderDetails.amount)} {orderDetails.currency?.toUpperCase() || 'USD'}
                      </Typography>
                    </Grid>
                    {orderDetails.subtotal && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2"><strong>Subtotal:</strong></Typography>
                        <Typography variant="body2">
                          ${orderDetails.subtotal.toFixed(2)}
                        </Typography>
                      </Grid>
                    )}
                    {orderDetails.obituaryId && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2"><strong>Obituary ID:</strong></Typography>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                          {orderDetails.obituaryId}
                        </Typography>
                      </Grid>
                    )}
                    {orderDetails.executionArn && (
                      <Grid item xs={12}>
                        <Typography variant="body2"><strong>Step Functions Execution ARN:</strong></Typography>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem', wordBreak: 'break-all' }}>
                          {orderDetails.executionArn}
                        </Typography>
                      </Grid>
                    )}
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2"><strong>Created:</strong></Typography>
                      <Typography variant="body2">
                        {formatDate(orderDetails.createdAt)}
                      </Typography>
                    </Grid>
                    {orderDetails.updatedAt && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2"><strong>Updated:</strong></Typography>
                        <Typography variant="body2">
                          {formatDate(orderDetails.updatedAt)}
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </CardContent>
              </Card>

              {/* Processing Steps */}
              {orderDetails.steps && (
                <Card variant="outlined" sx={{ mb: 3 }}>
                  <CardContent>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                      Processing Steps
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {Object.entries(orderDetails.steps).map(([step, status]) => (
                        <Box key={step} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Typography variant="body2" sx={{ minWidth: 200, textTransform: 'capitalize' }}>
                            {step.replace(/([A-Z])/g, ' $1').trim()}:
                          </Typography>
                          <Chip
                            label={status}
                            size="small"
                            color={
                              status === 'completed' ? 'success' :
                              status === 'failed' ? 'error' :
                              status === 'pending' ? 'warning' : 'default'
                            }
                          />
                        </Box>
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              )}

              {/* Error Details */}
              {orderDetails.error && (
                <Card variant="outlined" sx={{ mb: 3, borderColor: 'error.main' }}>
                  <CardContent>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: 'error.main' }}>
                      Error Details
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {orderDetails.error.step && (
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                            Failed Step:
                          </Typography>
                          <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                            {orderDetails.error.step.replace(/([A-Z])/g, ' $1').trim()}
                          </Typography>
                        </Box>
                      )}
                      {orderDetails.error.error && (
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                            Error Type:
                          </Typography>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                            {orderDetails.error.error}
                          </Typography>
                        </Box>
                      )}
                      {orderDetails.error.cause && (
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                            Error Message:
                          </Typography>
                          <Box
                            sx={{
                              p: 1.5,
                              bgcolor: 'error.light',
                              borderRadius: 1,
                              border: '1px solid',
                              borderColor: 'error.main',
                              maxHeight: 400,
                              overflow: 'auto'
                            }}
                          >
                            <Typography
                              variant="body2"
                              component="pre"
                              sx={{
                                fontFamily: 'monospace',
                                fontSize: '0.75rem',
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                                margin: 0
                              }}
                            >
                              {orderDetails.error.cause}
                            </Typography>
                          </Box>
                        </Box>
                      )}
                      {orderDetails.error.timestamp && (
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                            Error Timestamp:
                          </Typography>
                          <Typography variant="body2">
                            {formatDate(orderDetails.error.timestamp)}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              )}

              {/* Cart Items */}
              {orderDetails.cart && orderDetails.cart.length > 0 && (
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                      Cart Items ({orderDetails.cart.length})
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {orderDetails.cart.map((item, index) => (
                        <Box key={index} sx={{ p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                            Item {index + 1}
                          </Typography>
                          <Grid container spacing={1}>
                            {item.artwork && (
                              <Grid item xs={12}>
                                <Typography variant="body2"><strong>Artwork:</strong> {item.artwork}</Typography>
                              </Grid>
                            )}
                            {item.template && (
                              <Grid item xs={12}>
                                <Typography variant="body2"><strong>Template:</strong> {item.template}</Typography>
                              </Grid>
                            )}
                            {item.quantity && (
                              <Grid item xs={12} sm={6}>
                                <Typography variant="body2"><strong>Quantity:</strong> {item.quantity}</Typography>
                              </Grid>
                            )}
                            {item.price && (
                              <Grid item xs={12} sm={6}>
                                <Typography variant="body2"><strong>Price:</strong> {formatCurrency(item.price)}</Typography>
                              </Grid>
                            )}
                            {item.shippingMethod && (
                              <Grid item xs={12} sm={6}>
                                <Typography variant="body2"><strong>Shipping:</strong> {item.shippingMethod}</Typography>
                              </Grid>
                            )}
                            {item.recipient && (
                              <Grid item xs={12}>
                                <Typography variant="body2" sx={{ mt: 1 }}><strong>Recipient:</strong></Typography>
                                <Typography variant="body2" sx={{ pl: 2 }}>
                                  {item.recipient.firstName} {item.recipient.lastName}
                                  {item.recipient.address && (
                                    <>
                                      <br />{item.recipient.address}
                                      {item.recipient.address2 && <>, {item.recipient.address2}</>}
                                    </>
                                  )}
                                  {item.recipient.city && (
                                    <>
                                      <br />{item.recipient.city}
                                      {item.recipient.region && `, ${item.recipient.region}`}
                                      {item.recipient.postcode && ` ${item.recipient.postcode}`}
                                    </>
                                  )}
                                  {item.recipient.country && (
                                    <><br />{item.recipient.country}</>
                                  )}
                                </Typography>
                              </Grid>
                            )}
                          </Grid>
                        </Box>
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              )}

              {/* Link to Payment Transfers */}
              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  startIcon={<AccountBalanceIcon />}
                  onClick={() => {
                    navigate(`/admin-tools?tool=transfers&paymentId=${orderDetails.paymentIntentId}`);
                    handleCloseDetail();
                  }}
                >
                  View Payment Transfers
                </Button>
              </Box>
            </Box>
          ) : (
            <Alert severity="error">Failed to load order details</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDetail}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OrdersList;
